/**
 * Moving a row of a firm's list up or down — the order labels, the cancellation reasons.
 *
 * The API stores a number per row and sorts by it, then by name, so two rows may share one.
 * Swapping two numbers that are equal moves nothing; so a move renumbers the whole list in
 * tens, in the order the person now sees it, and sends only the rows whose number changed.
 * The first move on a list nobody ordered writes most of it; every move after writes two.
 */

export interface Ordered {
  id: string;
  sortOrder: number;
}

/** The new numbers after the row at `index` moves one place by `step`; none when it cannot. */
export function moved(
  rows: readonly Ordered[],
  index: number,
  step: -1 | 1,
): { id: string; sortOrder: number }[] {
  const target = index + step;

  if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) return [];

  const order = [...rows];

  [order[index], order[target]] = [order[target]!, order[index]!];

  return order
    .map((row, at) => ({ id: row.id, sortOrder: (at + 1) * 10, was: row.sortOrder }))
    .filter((row) => row.sortOrder !== row.was)
    .map(({ id, sortOrder }) => ({ id, sortOrder }));
}
